import PartySocket from "partysocket";
import { startAudioEngine, type AudioEngineHandle } from "./audio";
import { MeterBank, TARGET_DB } from "./meter";
import { navigate } from "./router";

type ServerMessage =
  | {
      type: "roster";
      you: string;
      participants: { id: string; name: string; joinedAt: number }[];
    }
  | { type: "join"; participant: { id: string; name: string; joinedAt: number } }
  | { type: "leave"; id: string }
  | { type: "rename"; id: string; name: string }
  | { type: "level"; id: string; rms: number; peak: number; t: number };

const NAME_KEY = "streamlevels:name";
const DEVICE_KEY = "streamlevels:deviceId";

const MUTE_DB_THRESHOLD = -55;
const MUTE_DWELL_MS = 2000;

const PARTY_HOST =
  import.meta.env.VITE_PARTY_HOST ??
  (import.meta.env.DEV ? "localhost:8787" : window.location.host);

export function renderRoom(root: HTMLElement, roomId: string): () => void {
  let myId: string | null = null;
  let myName = loadName();
  let audio: AudioEngineHandle | null = null;
  let socket: PartySocket | null = null;
  const meters = new MeterBank();
  const rows = new Map<string, RowRefs>();
  const muteState = new Map<
    string,
    { quietSince: number | null; muted: boolean }
  >();
  let muteTickHandle: number | null = null;
  let destroyed = false;

  type RowRefs = {
    root: HTMLElement;
    name: HTMLElement;
    db: HTMLElement;
    badge: HTMLElement;
    canvas: HTMLCanvasElement;
    participant: { id: string; name: string };
  };

  // ── Layout ───────────────────────────────────────────────────
  const wrap = el("div", { class: "room" });

  const header = el("header", { class: "room-header" });
  const left = el("div", { class: "left" });
  const brand = el("div", { class: "brand" });
  const mark = el("div", { class: "brand-mark" });
  for (let i = 0; i < 5; i++) mark.appendChild(el("span"));
  brand.appendChild(mark);
  brand.appendChild(el("div", { class: "brand-name" }, "StreamLevels"));
  left.appendChild(brand);
  left.appendChild(el("div", { class: "room-id" }, `lobby ${roomId}`));
  header.appendChild(left);

  const status = el("div", { class: "status-pill" });
  const statusDot = el("span", { class: "dot" });
  const statusText = el("span", {}, "Connecting…");
  status.appendChild(statusDot);
  status.appendChild(statusText);
  header.appendChild(status);

  wrap.appendChild(header);

  const share = el("section", { class: "toolbar" });
  share.appendChild(el("label", {}, "Share with co-streamers:"));
  const urlBox = el("span", { class: "url" }, window.location.href);
  share.appendChild(urlBox);
  const copyBtn = el("button", { class: "ghost" }, "Copy") as HTMLButtonElement;
  copyBtn.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      copyBtn.textContent = "Copied!";
      window.setTimeout(() => (copyBtn.textContent = "Copy"), 1500);
    } catch {
      // ignore
    }
  });
  share.appendChild(copyBtn);
  const leaveBtn = el(
    "button",
    { class: "danger" },
    "Leave",
  ) as HTMLButtonElement;
  leaveBtn.addEventListener("click", () => navigate("/"));
  share.appendChild(leaveBtn);
  wrap.appendChild(share);

  const toolbar = el("section", { class: "toolbar" });
  toolbar.appendChild(el("label", {}, "Your input:"));
  const deviceSelect = el("select") as HTMLSelectElement;
  deviceSelect.disabled = true;
  toolbar.appendChild(deviceSelect);
  toolbar.appendChild(
    el(
      "span",
      { class: "footnote" },
      `Aim to peak around ${Math.round(TARGET_DB)} dBFS (top of the yellow zone).`,
    ),
  );
  wrap.appendChild(toolbar);

  const scale = el("section", { class: "scale" });
  scale.appendChild(el("span"));
  const ticks = el("div", { class: "ticks" });
  for (const t of [-60, -42, -24, -18, -12, -6, 0]) {
    const norm = (t + 60) / 60;
    const span = el("span", {}, `${t}`);
    span.style.left = `${norm * 100}%`;
    ticks.appendChild(span);
  }
  scale.appendChild(ticks);
  scale.appendChild(el("span"));
  wrap.appendChild(scale);

  const board = el("section", { class: "board" });
  wrap.appendChild(board);

  const empty = el(
    "div",
    { class: "empty" },
    "Starting up…",
  );
  wrap.appendChild(empty);

  root.appendChild(wrap);

  // ── Boot sequence ─────────────────────────────────────────────
  void boot();

  // Dev-only debug hooks for verifying meter rendering without mic input.
  // Exposed on window in dev builds so they can be driven from devtools or MCP.
  if (import.meta.env.DEV) {
    interface MicCheckDebug {
      meters: MeterBank;
      addFake(id: string, name: string): void;
      removeFake(id: string): void;
      feed(id: string, rms: number, peak: number): void;
      autoplay(id: string, pattern: "speech" | "loud" | "quiet"): () => void;
    }
    (window as unknown as { __micCheckDebug: MicCheckDebug }).__micCheckDebug = {
      meters,
      addFake(id: string, name: string) {
        ensureRow({ id, name });
        refreshEmpty();
      },
      removeFake(id: string) {
        removeRow(id);
        refreshEmpty();
      },
      feed(id: string, rms: number, peak: number) {
        meters.update(id, { rms, peak });
        noteSampleForMute(id, rms);
        updateDbLabel(id);
      },
      autoplay(id: string, pattern: "speech" | "loud" | "quiet") {
        const start = performance.now();
        const handle = window.setInterval(() => {
          const t = (performance.now() - start) / 1000;
          let rms = -55;
          let peak = -55;
          if (pattern === "speech") {
            // Bursty mid-level: roughly -22 dBFS, with peaks to -10
            const env = (Math.sin(t * 4) + 1) / 2; // 0..1
            const breath = Math.sin(t * 0.6) > 0.4 ? 1 : 0.1; // pauses
            rms = -55 + env * breath * 35; // -55..-20
            peak = rms + 6 + Math.random() * 4;
          } else if (pattern === "loud") {
            const env = (Math.sin(t * 5) + 1) / 2;
            rms = -28 + env * 22;
            peak = rms + 4;
          } else {
            const env = (Math.sin(t * 2) + 1) / 2;
            rms = -60 + env * 10;
            peak = rms + 2;
          }
          meters.update(id, { rms, peak });
          noteSampleForMute(id, rms);
          updateDbLabel(id);
        }, 33);
        return () => window.clearInterval(handle);
      },
    };
  }

  async function boot() {
    try {
      muteTickHandle = window.setInterval(updateMuteIndicators, 250);

      if (import.meta.env.DEV && window.location.hash === "#nomic") {
        myName = "You";
        myId = "self";
        ensureRow({ id: myId, name: myName });
        setStatus("ok", "Demo mode");
        refreshEmpty();
        return;
      }

      if (!myName) {
        setEmpty("Pick a display name to join the lobby.");
        myName = await promptForName();
      }
      saveName(myName);

      setStatus("neutral", "Requesting mic…");
      setEmpty("Grant microphone access to continue (no audio is transmitted).");
      const savedDevice = localStorage.getItem(DEVICE_KEY) ?? undefined;
      try {
        audio = await startAudioEngine({
          deviceId: savedDevice,
          intervalMs: 33,
          onSample: handleLocalSample,
          onDeviceLost: () => {
            setStatus("bad", "Mic disconnected");
          },
        });
      } catch (err) {
        // If a remembered deviceId no longer exists, retry with default.
        if (
          savedDevice &&
          err instanceof DOMException &&
          (err.name === "OverconstrainedError" || err.name === "NotFoundError")
        ) {
          localStorage.removeItem(DEVICE_KEY);
          audio = await startAudioEngine({
            intervalMs: 33,
            onSample: handleLocalSample,
            onDeviceLost: () => setStatus("bad", "Mic disconnected"),
          });
        } else {
          throw err;
        }
      }

      await populateDevices();

      setStatus("neutral", "Connecting…");
      setEmpty("Connecting to lobby…");

      socket = new PartySocket({
        host: PARTY_HOST,
        room: roomId,
        query: { name: myName },
      });

      socket.addEventListener("open", () => {
        setStatus("ok", "Connected");
        refreshEmpty();
      });
      socket.addEventListener("close", () =>
        setStatus("bad", "Reconnecting…"),
      );
      socket.addEventListener("error", () =>
        setStatus("bad", "Connection issue"),
      );
      socket.addEventListener("message", (e) => {
        try {
          handleServerMessage(JSON.parse(e.data) as ServerMessage);
        } catch (err) {
          console.error("bad message", err);
        }
      });

    } catch (err) {
      renderError(err);
    }
  }

  // ── Audio sample handling ─────────────────────────────────────
  function handleLocalSample(s: { rms: number; peak: number }) {
    if (myId) {
      meters.update(myId, s);
      noteSampleForMute(myId, s.rms);
      updateDbLabel(myId);
    }
    socket?.send(
      JSON.stringify({ type: "level", rms: s.rms, peak: s.peak, t: Date.now() }),
    );
  }

  function handleServerMessage(msg: ServerMessage) {
    switch (msg.type) {
      case "roster": {
        myId = msg.you;
        const me: { id: string; name: string; joinedAt: number } = {
          id: msg.you,
          name: myName,
          joinedAt: Date.now(),
        };
        const all = [me, ...msg.participants.filter((p) => p.id !== msg.you)];
        for (const p of all) ensureRow(p);
        refreshEmpty();
        return;
      }
      case "join":
        ensureRow(msg.participant);
        refreshEmpty();
        return;
      case "leave":
        removeRow(msg.id);
        refreshEmpty();
        return;
      case "rename": {
        const r = rows.get(msg.id);
        if (r) {
          r.participant.name = msg.name;
          r.name.firstChild!.textContent = msg.name;
        }
        return;
      }
      case "level":
        meters.update(msg.id, { rms: msg.rms, peak: msg.peak });
        noteSampleForMute(msg.id, msg.rms);
        updateDbLabel(msg.id);
        return;
    }
  }

  // ── DOM helpers ───────────────────────────────────────────────
  function ensureRow(p: { id: string; name: string }) {
    if (rows.has(p.id)) return;
    const row = el("div", { class: "row" });
    if (p.id === myId) row.classList.add("you");

    const nameNode = el("div", { class: "name" });
    const nameText = document.createTextNode(p.name);
    nameNode.appendChild(nameText);
    if (p.id === myId) {
      nameNode.appendChild(el("span", { class: "you-tag" }, "you"));
      nameNode.style.cursor = "pointer";
      nameNode.title = "Click to rename";
      nameNode.addEventListener("click", () => void renameMe(nameText));
    }
    row.appendChild(nameNode);

    const meterWrap = el("div", { class: "meter-wrap" });
    const canvas = document.createElement("canvas");
    canvas.className = "meter";
    meterWrap.appendChild(canvas);
    row.appendChild(meterWrap);

    const db = el("div", { class: "db" }, "—∞");
    row.appendChild(db);

    const badge = el("div", { class: "badge" }, "");
    badge.style.display = "none";
    row.appendChild(badge);

    board.appendChild(row);
    meters.attach(p.id, canvas);
    rows.set(p.id, {
      root: row,
      name: nameNode,
      db,
      badge,
      canvas,
      participant: { id: p.id, name: p.name },
    });
    muteState.set(p.id, { quietSince: null, muted: false });
  }

  function removeRow(id: string) {
    const r = rows.get(id);
    if (!r) return;
    meters.detach(id);
    r.root.remove();
    rows.delete(id);
    muteState.delete(id);
  }

  function refreshEmpty() {
    const otherCount = [...rows.keys()].filter((k) => k !== myId).length;
    if (otherCount === 0) {
      setEmpty("Waiting for co-streamers… Send them the URL above.");
      empty.style.display = "";
    } else {
      empty.style.display = "none";
    }
  }

  function setEmpty(text: string) {
    empty.textContent = text;
  }

  function updateDbLabel(id: string) {
    const r = rows.get(id);
    if (!r) return;
    const v = meters.currentDb(id);
    r.db.textContent = v <= -59.5 ? "—∞" : `${v.toFixed(0)} dB`;
    r.root.classList.toggle("stale", meters.isStale(id));
  }

  function noteSampleForMute(id: string, db: number) {
    const s = muteState.get(id);
    if (!s) return;
    const now = performance.now();
    if (db < MUTE_DB_THRESHOLD) {
      if (s.quietSince === null) s.quietSince = now;
    } else {
      s.quietSince = null;
      if (s.muted) {
        s.muted = false;
        updateBadge(id);
      }
    }
  }

  function updateMuteIndicators() {
    const now = performance.now();
    for (const [id, s] of muteState) {
      if (
        s.quietSince !== null &&
        now - s.quietSince > MUTE_DWELL_MS &&
        !s.muted
      ) {
        s.muted = true;
        updateBadge(id);
      }
      // Mark stale rows visually too
      const r = rows.get(id);
      if (r) r.root.classList.toggle("stale", meters.isStale(id, now));
    }
  }

  function updateBadge(id: string) {
    const r = rows.get(id);
    const s = muteState.get(id);
    if (!r || !s) return;
    if (s.muted) {
      r.badge.textContent = id === myId ? "You're muted?" : "Possibly muted";
      r.badge.className = "badge muted";
      r.badge.style.display = "";
    } else {
      r.badge.style.display = "none";
    }
  }

  function setStatus(kind: "ok" | "bad" | "neutral", text: string) {
    status.classList.remove("ok", "bad");
    if (kind === "ok") status.classList.add("ok");
    else if (kind === "bad") status.classList.add("bad");
    statusText.textContent = text;
  }

  async function renameMe(textNode: Text) {
    const next = await askForName({
      title: "Change your display name",
      body: "Show this name to others in the lobby.",
      submitLabel: "Save",
      initial: myName,
      cancellable: true,
    });
    if (!next || next === myName) return;
    myName = next;
    saveName(myName);
    textNode.textContent = myName;
    socket?.send(JSON.stringify({ type: "rename", name: myName }));
  }

  async function promptForName(): Promise<string> {
    const seed = `Streamer ${Math.floor(Math.random() * 900 + 100)}`;
    return askForName({
      title: "What's your display name?",
      body: "Visible to everyone in this lobby. You can change it later.",
      submitLabel: "Join lobby",
      initial: seed,
      cancellable: false,
    });
  }

  function askForName(opts: {
    title: string;
    body: string;
    submitLabel: string;
    initial: string;
    cancellable: boolean;
  }): Promise<string> {
    return new Promise((resolve) => {
      const overlay = el("div", { class: "overlay" });
      const panel = el("div", { class: "panel" });
      panel.appendChild(el("h2", {}, opts.title));
      panel.appendChild(el("p", {}, opts.body));
      const form = document.createElement("form");
      form.className = "row-form";
      const input = document.createElement("input");
      input.type = "text";
      input.value = opts.initial;
      input.maxLength = 32;
      input.placeholder = "Your name";
      input.autocomplete = "off";
      const submit = el(
        "button",
        { type: "submit" },
        opts.submitLabel,
      ) as HTMLButtonElement;
      form.appendChild(input);
      form.appendChild(submit);
      if (opts.cancellable) {
        const cancel = el(
          "button",
          { class: "ghost", type: "button" },
          "Cancel",
        ) as HTMLButtonElement;
        cancel.addEventListener("click", () => {
          overlay.remove();
          resolve(opts.initial);
        });
        form.appendChild(cancel);
      }
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        const value = input.value.trim() || opts.initial;
        overlay.remove();
        resolve(value);
      });
      panel.appendChild(form);
      overlay.appendChild(panel);
      document.body.appendChild(overlay);
      requestAnimationFrame(() => {
        input.focus();
        input.select();
      });
    });
  }

  async function populateDevices() {
    if (!audio) return;
    try {
      const inputs = await audio.listInputDevices();
      deviceSelect.replaceChildren();
      const current = audio.getCurrentDeviceId();
      let matched = false;
      inputs.forEach((d, i) => {
        const opt = document.createElement("option");
        opt.value = d.deviceId;
        opt.textContent =
          d.label || `Microphone ${i + 1}${d.deviceId ? "" : " (default)"}`;
        if (d.deviceId === current) {
          opt.selected = true;
          matched = true;
        }
        deviceSelect.appendChild(opt);
      });
      if (!matched && inputs[0]) deviceSelect.value = inputs[0].deviceId;
      deviceSelect.disabled = false;
      deviceSelect.onchange = async () => {
        if (!audio) return;
        const id = deviceSelect.value;
        try {
          await audio.switchDevice(id);
          localStorage.setItem(DEVICE_KEY, id);
        } catch (err) {
          console.error("switchDevice failed", err);
          setStatus("bad", "Couldn't open that device");
        }
      };
    } catch (err) {
      console.warn("enumerateDevices failed", err);
    }
  }

  function renderError(err: unknown) {
    const msg =
      err instanceof Error ? err.message : "Couldn't start the lobby.";
    const isPermission =
      err instanceof DOMException &&
      (err.name === "NotAllowedError" || err.name === "SecurityError");
    const isNoDevice =
      err instanceof DOMException &&
      (err.name === "NotFoundError" || err.name === "OverconstrainedError");
    const error = el("div", { class: "error" });
    if (isPermission) {
      error.appendChild(el("strong", {}, "Microphone permission required. "));
      error.appendChild(
        document.createTextNode(
          "Grant access in your browser site settings and reload.",
        ),
      );
    } else if (isNoDevice) {
      error.appendChild(el("strong", {}, "No input device found. "));
      error.appendChild(
        document.createTextNode(
          "Plug in a microphone (or select a different device) and reload.",
        ),
      );
    } else {
      error.appendChild(el("strong", {}, "Couldn't start: "));
      error.appendChild(document.createTextNode(msg));
    }
    wrap.replaceChildren(error);
  }

  // ── Cleanup ───────────────────────────────────────────────────
  return () => {
    if (destroyed) return;
    destroyed = true;
    if (muteTickHandle !== null) window.clearInterval(muteTickHandle);
    socket?.close();
    socket = null;
    void audio?.stop();
    audio = null;
    meters.destroy();
    rows.clear();
    muteState.clear();
  };
}

function loadName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

function saveName(n: string) {
  try {
    localStorage.setItem(NAME_KEY, n);
  } catch {
    // ignore
  }
}

function el(
  tag: string,
  attrs: Record<string, string> = {},
  text?: string,
): HTMLElement {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (text !== undefined) node.textContent = text;
  return node;
}
